import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import { API_URL } from "../../utils/api-config";

interface FuelCard {
  _id: string;
  cardNumber: string;
  provider: string;
}

interface Vehicle {
  _id: string;
  plate: string;
  brand: string;
  model: string;
}

export default function FuelCardAssignVehicle() {
  const [cardNumber, setCardNumber] = useState("");
  const [vehiclePlate, setVehiclePlate] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cards, setCards] = useState<FuelCard[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoadingData(true);
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        
        // Récupérer la liste des cartes
        const cardsRes = await fetch(`${API_URL}/fuel-cards`, { headers });
        const cardsData = await cardsRes.json();
        setCards(Array.isArray(cardsData) ? cardsData : []);

        // Récupérer la liste des véhicules
        const vehiclesRes = await fetch(`${API_URL}/vehicles`, { headers });
        const vehiclesData = await vehiclesRes.json();
        setVehicles(Array.isArray(vehiclesData) ? vehiclesData : []);
      } catch (err) {
        console.error("Erreur lors du chargement des données", err);
        setError("Erreur lors du chargement des données");
        setCards([]);
        setVehicles([]);
      } finally {
        setLoadingData(false);
      }
    };

    fetchData();
  }, []);

  const handleCardChange = (event: SelectChangeEvent) => {
    setCardNumber(event.target.value);
  };

  const handleVehicleChange = (event: SelectChangeEvent) => {
    setVehiclePlate(event.target.value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cardNumber || !vehiclePlate) {
      setError("Veuillez sélectionner une carte et un véhicule");
      return;
    }

    setLoading(true);
    setSuccess(null);
    setError(null);
    try {
      const res = await fetch(
        `${API_URL}/fuel-cards/${cardNumber}/assign-vehicle`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ vehiclePlate }),
        }
      );
      if (!res.ok) throw new Error("Erreur lors de l'association");
      setSuccess("Carte associée au véhicule avec succès !");
      setCardNumber("");
      setVehiclePlate("");
    } catch {
      setError("Erreur lors de l'association de la carte au véhicule");
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ maxWidth: 400, mx: "auto" }}
    >
      <Typography variant="h6" sx={{ mb: 2 }}>
        Associer une carte à un véhicule
      </Typography>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="card-select-label">Carte</InputLabel>
        <Select
          labelId="card-select-label"
          value={cardNumber}
          label="Carte"
          onChange={handleCardChange}
          required
        >
          {cards.map((card) => (
            <MenuItem key={card._id} value={card.cardNumber}>
              {card.cardNumber} - {card.provider}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="vehicle-select-label">Véhicule</InputLabel>
        <Select
          labelId="vehicle-select-label"
          value={vehiclePlate}
          label="Véhicule"
          onChange={handleVehicleChange}
          required
        >
          {vehicles.map((vehicle) => (
            <MenuItem key={vehicle._id} value={vehicle.plate}>
              {vehicle.plate} - {vehicle.brand} {vehicle.model}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Button
        type="submit"
        variant="contained"
        color="primary"
        fullWidth
        disabled={loading}
      >
        {loading ? <CircularProgress size={24} /> : "Associer"}
      </Button>
    </Box>
  );
}
